import { z } from 'zod';
import { DUE_PAYMENT_MODES, PAYMENT_MODES } from '@shripos/shared/constants';
import { Field, Input, Select, errorAt } from '@shripos/ui';
import { FormDialog } from '../../components/FormDialog.jsx';
import { api } from '../../lib/api.js';
import { rupees, toPaise, toRupeeInput } from '../../lib/money.js';

const form = z.object({
  amount: z.string().trim().min(1, 'Enter an amount'),
  mode: z.enum(DUE_PAYMENT_MODES),
  reference: z.string().trim().max(60).optional(),
  note: z.string().trim().max(200).optional(),
});

/** Take a payment against what a customer owes. Cash goes into this device's open shift. */
export function CollectDueDialog({ customer, open, onClose, onDone }) {
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={`Collect dues · ${customer?.name ?? customer?.phone ?? ''}`}
      description={customer ? `Owes ${rupees(customer.duePaise)}. Cash is added to your drawer.` : undefined}
      schema={form}
      defaultValues={{ amount: toRupeeInput(customer?.duePaise ?? 0), mode: 'cash', reference: '', note: '' }}
      submitLabel="Record payment"
      onSubmit={async (v) => {
        const amountPaise = toPaise(v.amount);
        const res = await api.post(`/customers/${customer.id}/dues`, {
          amountPaise,
          mode: v.mode,
          reference: v.reference || null,
          note: v.note || null,
        });
        onDone?.(res);
      }}
    >
      {({ register, watch, formState: { errors } }) => (
        <>
          <Field label="Amount (₹)" error={errorAt(errors, 'amount') ?? errorAt(errors, 'amountPaise')}>
            <Input inputMode="decimal" autoFocus {...register('amount')} />
          </Field>
          <Field label="Paid by" error={errorAt(errors, 'mode')}>
            <Select {...register('mode')}>
              {DUE_PAYMENT_MODES.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_MODES.find((p) => p.key === m)?.label ?? m}
                </option>
              ))}
            </Select>
          </Field>
          {watch('mode') !== 'cash' && (
            <Field label="Reference (UTR / last 4 digits)" error={errorAt(errors, 'reference')}>
              <Input {...register('reference')} />
            </Field>
          )}
          <Field label="Note (optional)" error={errorAt(errors, 'note')}>
            <Input {...register('note')} />
          </Field>
        </>
      )}
    </FormDialog>
  );
}
